// ============================================================================
// Google Calendar — OAuth 2.1 helpers.
//
// Flow summary (Google Identity Services OAuth 2.1 for Installed Apps):
//   1. `buildGoogleCalendarAuthorizeUrl(...)` → redirect browser to the
//      Google consent dialog. The platform PKCE engine
//      (`services/oauth.service.ts`) stores the state row in
//      `connectors_oauth_states`. We add `access_type=offline` +
//      `prompt=consent` so Google always issues a refresh token AND rotates
//      incremental consent correctly, and `include_granted_scopes=true` to
//      preserve grants from prior Google OAuth flows in this app.
//   2. Google returns `?code=...&state=...` to our callback. The platform
//      route calls `exchangeGoogleCode(...)` → we receive an access +
//      refresh token pair.
//   3. `refreshGoogleToken(...)` swaps a refresh token for a fresh access
//      token. Google refresh tokens do NOT expire unless the user revokes
//      consent or 6 months pass without use.
//   4. `validateAccessToken(...)` verifies a live token by calling Google's
//      tokeninfo endpoint. Useful for the admin "test connection" button.
//
// References:
//   - https://developers.google.com/identity/protocols/oauth2/web-server
//   - https://developers.google.com/calendar/api/guides/auth
//   - https://developers.google.com/identity/protocols/oauth2#expiration
// ============================================================================

import type { DrizzleD1Database } from 'drizzle-orm/d1'

import {
  buildAuthorizeUrl as coreBuildAuthorizeUrl,
  type BuildAuthorizeUrlInput as CoreBuildAuthorizeUrlInput,
  type BuildAuthorizeUrlResult as CoreBuildAuthorizeUrlResult,
} from '../../services/oauth.service'
import { GoogleCalendarError, sanitizeGoogleErrorMessage } from './types'

// ============================================================================
// Constants
// ============================================================================

/** Google OAuth 2.1 authorize endpoint. */
export const GOOGLE_AUTHORIZE_URL = 'https://accounts.google.com/o/oauth2/v2/auth'
/** Google OAuth 2.1 token endpoint (exchange + refresh). */
export const GOOGLE_TOKEN_URL = 'https://oauth2.googleapis.com/token'
/** Google tokeninfo endpoint — returns liveness + granted scopes for a token. */
export const GOOGLE_TOKENINFO_URL = 'https://www.googleapis.com/oauth2/v3/tokeninfo'
/** Canonical Google Calendar API base. */
export const GOOGLE_CALENDAR_API_BASE = 'https://www.googleapis.com/calendar/v3'

/**
 * Default scope set for the Google Calendar adapter.
 *
 * We request BOTH scopes because:
 *   - `.../calendar`        → read/write access to all calendars (needed to
 *                              list calendars + create events on non-primary).
 *   - `.../calendar.events` → fine-grained event CRUD (Google recommends this
 *                              alongside the broader scope for clearer audit
 *                              logs on the consent screen).
 *
 * Using `include_granted_scopes=true` means the access token Google issues
 * preserves ALL previously granted scopes for this client, so adding more
 * later is non-disruptive.
 */
export const GOOGLE_CALENDAR_DEFAULT_SCOPES = [
  'https://www.googleapis.com/auth/calendar',
  'https://www.googleapis.com/auth/calendar.events',
] as const

// ============================================================================
// buildGoogleCalendarAuthorizeUrl
// ============================================================================

export type GoogleCalendarAuthorizeInput = Omit<
  CoreBuildAuthorizeUrlInput,
  'authorizeUrl' | 'scopes' | 'extraParams'
> & {
  /** Override the default scopes. */
  scopes?: readonly string[]
  /** Extra params merged into the authorize URL (e.g. `login_hint`). */
  extraParams?: Record<string, string>
}

export type GoogleCalendarAuthorizeResult = CoreBuildAuthorizeUrlResult

/**
 * Build the Google OAuth 2.1 authorize URL + persist a PKCE state row.
 *
 * Google-specific extras layered on top of the core engine:
 *   - `access_type=offline`        — required to receive a refresh token.
 *   - `prompt=consent`             — force the consent screen so a refresh
 *                                     token is issued EVERY time (Google will
 *                                     otherwise skip refresh issuance for
 *                                     returning users in the same session).
 *   - `include_granted_scopes=true` — incremental consent; preserves prior
 *                                     grants from this client.
 */
export async function buildGoogleCalendarAuthorizeUrl(
  opts: GoogleCalendarAuthorizeInput,
  db: DrizzleD1Database,
  now: Date = new Date(),
): Promise<GoogleCalendarAuthorizeResult> {
  const scopes = (opts.scopes && opts.scopes.length > 0
    ? opts.scopes
    : GOOGLE_CALENDAR_DEFAULT_SCOPES) as readonly string[]

  return coreBuildAuthorizeUrl(
    {
      ...opts,
      authorizeUrl: GOOGLE_AUTHORIZE_URL,
      scopes: [...scopes],
      extraParams: {
        access_type: 'offline',
        prompt: 'consent',
        include_granted_scopes: 'true',
        ...(opts.extraParams ?? {}),
      },
    },
    db,
    now,
  )
}

// ============================================================================
// exchangeGoogleCode
// ============================================================================

export interface GoogleExchangeCodeInput {
  code: string
  clientId: string
  /** Google confidential clients (web apps) require client_secret on exchange. */
  clientSecret: string
  redirectUri: string
  /** Override the token endpoint (tests). */
  tokenUrl?: string
  /** PKCE code_verifier — Google requires this when the authorize URL used S256. */
  codeVerifier?: string
}

export interface GoogleExchangeCodeResult {
  accessToken: string
  /** Seconds until expiry. Google issues 1h access tokens. */
  expiresIn: number
  /** Absolute expiry derived from `expires_in` + call time. ISO 8601 UTC. */
  expiresAt: string
  /** Present on first exchange (or when `prompt=consent` forces a re-grant). */
  refreshToken?: string
  /** Space-separated list of granted scopes. */
  scope?: string
  tokenType?: string
  /** OIDC id token — only present when `openid` scope requested (we don't by default). */
  idToken?: string
}

interface GoogleTokenResponseBody {
  access_token?: string
  expires_in?: number
  refresh_token?: string
  id_token?: string
  scope?: string
  token_type?: string
  error?: string
  error_description?: string
}

/**
 * Exchange an authorization code for tokens via
 * `POST https://oauth2.googleapis.com/token`.
 *
 * Per Google's OAuth 2.1 implementation:
 *   - Content-Type: application/x-www-form-urlencoded
 *   - grant_type=authorization_code
 *   - client_secret goes in the body (NOT the URL) — Google accepts HTTP Basic
 *     too but we stick with the body form for symmetry with the other adapters.
 */
export async function exchangeGoogleCode(
  input: GoogleExchangeCodeInput,
  fetchImpl: typeof fetch = fetch,
  now: Date = new Date(),
): Promise<GoogleExchangeCodeResult> {
  assertNonEmpty(input.code, 'code')
  assertNonEmpty(input.clientId, 'clientId')
  assertNonEmpty(input.clientSecret, 'clientSecret')
  assertNonEmpty(input.redirectUri, 'redirectUri')

  const url = input.tokenUrl ?? GOOGLE_TOKEN_URL
  const params = new URLSearchParams({
    grant_type: 'authorization_code',
    code: input.code,
    redirect_uri: input.redirectUri,
    client_id: input.clientId,
    client_secret: input.clientSecret,
  })
  if (input.codeVerifier) {
    params.set('code_verifier', input.codeVerifier)
  }

  const resp = await fetchImpl(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  })

  if (!resp.ok) {
    const errText = await safeReadText(resp)
    // Error response bodies frequently echo the `code` or other secrets back;
    // scrub before logging.
    console.error(
      '[connectors/google-calendar/oauth] exchangeGoogleCode failed',
      resp.status,
      redactGoogleSecrets(errText).slice(0, 500),
    )
    throw new GoogleCalendarError(
      'AUTH_FAILED',
      `Google token exchange failed (HTTP ${resp.status})`,
      { httpStatus: resp.status },
    )
  }

  const body = (await resp.json().catch(() => null)) as
    | GoogleTokenResponseBody
    | null
  if (!body || typeof body.access_token !== 'string' || body.access_token.length === 0) {
    throw new GoogleCalendarError(
      'AUTH_FAILED',
      'Google did not return access_token',
    )
  }

  // Google's Calendar OAuth issues 3600s (1h) access tokens. Fall back to
  // 3600 if the field is missing so callers always have a usable expiry.
  const expiresIn = typeof body.expires_in === 'number' ? body.expires_in : 3600
  const expiresAt = new Date(now.getTime() + expiresIn * 1000).toISOString()

  return {
    accessToken: body.access_token,
    expiresIn,
    expiresAt,
    refreshToken: typeof body.refresh_token === 'string' ? body.refresh_token : undefined,
    idToken: typeof body.id_token === 'string' ? body.id_token : undefined,
    scope: typeof body.scope === 'string' ? body.scope : undefined,
    tokenType: typeof body.token_type === 'string' ? body.token_type : undefined,
  }
}

// ============================================================================
// refreshGoogleToken
// ============================================================================

export interface RefreshGoogleTokenInput {
  refreshToken: string
  clientId: string
  /** Google confidential clients require this on refresh too. */
  clientSecret: string
  /** Override the token endpoint (tests). */
  tokenUrl?: string
}

/**
 * Refresh a Google Calendar access token.
 *
 * IMPORTANT: Google does NOT always return a new refresh token on refresh.
 * Callers should persist the returned `refreshToken` only when present, and
 * KEEP the prior one otherwise. The dispatcher (`dispatcher.service.ts →
 * refreshAndPersist`) already follows this pattern.
 *
 * Google refresh tokens can silently expire when:
 *   - the user revokes consent in their Google Account
 *   - the token is unused for 6 months
 *   - the user changes their Google password (only for accounts with
 *     pre-2FA settings)
 * In any of these cases the refresh call returns 400 + `invalid_grant`,
 * which `classifyGoogleError` maps to INVALID_PAYLOAD. Callers should
 * surface that as "re-authenticate required".
 */
export async function refreshGoogleToken(
  input: RefreshGoogleTokenInput,
  fetchImpl: typeof fetch = fetch,
  now: Date = new Date(),
): Promise<GoogleExchangeCodeResult> {
  assertNonEmpty(input.refreshToken, 'refreshToken')
  assertNonEmpty(input.clientId, 'clientId')
  assertNonEmpty(input.clientSecret, 'clientSecret')

  const url = input.tokenUrl ?? GOOGLE_TOKEN_URL
  const params = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: input.refreshToken,
    client_id: input.clientId,
    client_secret: input.clientSecret,
  })

  const resp = await fetchImpl(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  })

  if (!resp.ok) {
    const errText = await safeReadText(resp)
    console.error(
      '[connectors/google-calendar/oauth] refreshGoogleToken failed',
      resp.status,
      redactGoogleSecrets(errText).slice(0, 500),
    )
    throw new GoogleCalendarError(
      'AUTH_FAILED',
      `Google token refresh failed (HTTP ${resp.status})`,
      { httpStatus: resp.status },
    )
  }

  const body = (await resp.json().catch(() => null)) as
    | GoogleTokenResponseBody
    | null
  if (!body || typeof body.access_token !== 'string' || body.access_token.length === 0) {
    throw new GoogleCalendarError(
      'AUTH_FAILED',
      'Google did not return access_token on refresh',
    )
  }

  const expiresIn = typeof body.expires_in === 'number' ? body.expires_in : 3600
  const expiresAt = new Date(now.getTime() + expiresIn * 1000).toISOString()

  return {
    accessToken: body.access_token,
    expiresIn,
    expiresAt,
    // May be absent — only some refresh responses rotate the refresh token.
    refreshToken: typeof body.refresh_token === 'string' ? body.refresh_token : undefined,
    scope: typeof body.scope === 'string' ? body.scope : undefined,
    tokenType: typeof body.token_type === 'string' ? body.token_type : undefined,
  }
}

// ============================================================================
// validateAccessToken — live token check via tokeninfo
// ============================================================================

export interface GoogleTokenInfo {
  /** Seconds until expiry. */
  expiresIn: number
  /** Space-separated list of granted scopes. */
  scope: string
  /** Google account id. */
  sub?: string
  /** Google app (client) id the token was issued for. */
  azp?: string
  email?: string
}

/**
 * Validate a Google access token via the tokeninfo endpoint. Returns the
 * token metadata on success, throws `GoogleCalendarError('AUTH_FAILED')` if
 * Google reports the token invalid / expired.
 *
 * Good for the admin "test connection" button: a 200 with live `expires_in`
 * means the token works; any non-2xx means re-auth is required.
 *
 * WARNING: `tokeninfo` puts the token in the QUERY STRING, not a header.
 * Cloudflare Workers Logpush DOES NOT log query strings by default (Cf
 * redacts them in the default log schema), but if your deployment has
 * enabled "log the raw URL" in Logpush config you MUST disable that for the
 * Worker that calls this function, or tokens will leak. The default posture
 * (no URL logging) is safe. We use this endpoint because Google offers no
 * POST-body equivalent — the token MUST go on the wire somehow, and a query
 * string within a TLS-protected request to Google is the documented shape.
 */
export async function validateAccessToken(
  accessToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<GoogleTokenInfo> {
  assertNonEmpty(accessToken, 'accessToken')

  // URLSearchParams percent-encodes safely.
  const url = `${GOOGLE_TOKENINFO_URL}?access_token=${encodeURIComponent(accessToken)}`
  const resp = await fetchImpl(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  })

  if (!resp.ok) {
    // Don't log the response body here — tokeninfo error bodies can echo
    // the token parameter back. `redactGoogleSecrets` would strip it but we
    // skip logging entirely for belt-and-braces.
    throw new GoogleCalendarError(
      resp.status === 401 || resp.status === 400
        ? 'AUTH_FAILED'
        : 'PROVIDER_ERROR',
      `Google tokeninfo failed (HTTP ${resp.status})`,
      { httpStatus: resp.status },
    )
  }

  interface TokenInfoRaw {
    azp?: string
    aud?: string
    sub?: string
    scope?: string
    exp?: string
    expires_in?: string | number
    email?: string
    email_verified?: string
  }
  const body = (await resp.json().catch(() => null)) as TokenInfoRaw | null
  if (!body || typeof body.scope !== 'string' || body.scope.length === 0) {
    throw new GoogleCalendarError(
      'AUTH_FAILED',
      'Google tokeninfo returned an unexpected shape',
    )
  }

  const expiresIn = typeof body.expires_in === 'string'
    ? Number(body.expires_in)
    : typeof body.expires_in === 'number'
      ? body.expires_in
      : 0

  return {
    expiresIn: Number.isFinite(expiresIn) ? expiresIn : 0,
    scope: body.scope,
    sub: typeof body.sub === 'string' ? body.sub : undefined,
    azp: typeof body.azp === 'string' ? body.azp : undefined,
    email: typeof body.email === 'string' ? body.email : undefined,
  }
}

// ============================================================================
// redactGoogleSecrets — re-export of the sanitiser for external callers
// ============================================================================

/**
 * Alias for `sanitizeGoogleErrorMessage`. Exported with the more idiomatic
 * name so callers don't need to know where the patterns live. Consumers
 * that log Google API responses should funnel strings through this before
 * `console.log` / `console.error`.
 */
export function redactGoogleSecrets(raw: string): string {
  return sanitizeGoogleErrorMessage(raw)
}

// ============================================================================
// Helpers
// ============================================================================

function assertNonEmpty(value: string | undefined | null, field: string): void {
  if (typeof value !== 'string' || value.length === 0) {
    throw new GoogleCalendarError('INVALID_INPUT', `${field} is required`)
  }
}

async function safeReadText(resp: Response): Promise<string> {
  try {
    return await resp.text()
  } catch {
    return ''
  }
}
